<template>
    <div v-if="config.display" v-show="!config.hideControl" :style="{'width': config.width || '100%'}" class="control e-control">
        <label-wrapper :label-settings="config"></label-wrapper>
        <div v-if="asField">
            <display-value :id="config.id" :value="value"></display-value>
        </div>
        <div v-else>
            <div v-if="!optionsReady" class="loading-message">
                Getting data. Please wait...
            </div>
            <div v-else :class="{'flex' : config.showSecondaryActionsSeparately}">
                <div class="dropdown-wrapper">
                    <ejs-dropdownlist
                        v-bind="syncfusionConfig"
                        v-model="value"
                        :value="value"
                        :dataSource="options"
                        :readonly="config.readonly || config.disabled"
                        :floatLabelType="config.floatLabelType || 'Never'"
                        :placeholder="ddStoreFilter(config.placeholder)"
                        @click="action($event, 'onclick')"
                        @focus="action($event, 'onfocus')"
                        @change="localChange($event);action($event, 'onchange', config)"
                        @select="action($event, 'onselect', config)"
                        @filtering="localFiltering($event); action($event, 'onfiltering')"
                        @created="created"
                        @beforeOpen="beforeOpen"
                        @actionComplete="actionComplete"
                        @close="localClose"
                        :ref="ref"
                        :data-test-id="dataTestId"
                        :enabled="!config.disabled"
                        :showClearButton="showClearButton"
                        :itemTemplate="itemTemplate"
                        :valueTemplate="valueTemplate"
                        :filterType="'contains'"
                    ></ejs-dropdownlist>
                </div>
                <div class="sub-content-area">
                    <div class="message-container" v-if="config.message">
                        <p class="input-info" v-html="ddStoreFilter(config.message)"></p>
                    </div>
                    <div class="secondary-action-container" v-if="config.secondaryActions">
                        <div v-if="config.showSecondaryActionsSeparately" :class="{'flex' : config.showSecondaryActionsSeparately}">
                            <div v-for="(item, index) in config.secondaryActions" :key="index" class="separately-secondary-action-button-wrapper">
                                <control :name="this.id + 'SECONDATY_ACTION_' + index" :prop-config="{
                                    disabled: config.disabled,
                                    label: item.text,
                                    type: 'button',
                                    logic: item.logic
                                }"
                                @click="runSecondaryAction($event, index)"
                                ></control>
                            </div>
                        </div>
                        <div v-else>
                            <ejs-splitbutton
                                :disabled="config.disabled"
                                :content="config.secondaryActions[0].text"
                                :items="config.secondaryActions.slice(1)"
                                @click="runSecondaryAction($event)"
                                @select="runSecondaryAction($event)"
                                class="secondary-action-button"
                            ></ejs-splitbutton>
                        </div>
                    </div>
                    <sam-tooltip
                        v-if="missingOptions.length"
                        ref="tooltip"
                        opensOn="Click"
                        content="<strong>Note:</strong> One or more selected values does not match any available options."
                        label="options missing"
                        label-classes=""
                        :id="config.id"
                    ></sam-tooltip>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineAsyncComponent } from "@common/defineAsyncComponent";
import SamTooltipComponent from "./samTooltip.vue";
import { controlMixins } from "./controlMixins.js";
import { recordStore } from "@common/recordStore";

export const dropdownComponent = {
    mixins: [controlMixins],
    computed: {
        showClearButton() {
            return typeof this.config.showClearButton !== 'undefined' ? this.config.showClearButton : true;
        },
        itemTemplate() {
            if (!this.config.itemTemplate) return false;
            let templateId = 'itemTemplate_' + this.config.controlStoreIndex;

            let tmpCmp = vueApp.component(templateId, {
                template: `<div>${this.config.itemTemplate}</div>`,
                data() {
                    return {
                        config: this.config,
                        data: {}
                    };
                },
                computed: {
                    record() {
                        // This is needed to access option data asynchronously
                        return this.data;
                    },
                    records() {
                        // For autosubscribe data in the dropdown list item template
                        return recordStore().records;
                    }
                }
            });
            let tmpFunc = function () {
                return {
                    template: tmpCmp
                };
            };
            Object.defineProperty(tmpFunc, "name", { value: templateId });
            return tmpFunc;
        },
        valueTemplate() {
            if (!this.config.valueTemplate) return false;
            let templateId = 'valueTemplate_' + this.config.name;

            let tmpCmp = vueApp.component(templateId, {
                template: `<div>${this.config.valueTemplate}</div>`,
                data() {
                    return {
                        config: this.config,
                        data: {}
                    };
                }
            });
            let tmpFunc = function () {
                return {
                    template: tmpCmp
                };
            };
            Object.defineProperty(tmpFunc, "name", { value: templateId });
            return tmpFunc;
        },
        ref() {
            return this.config.ref || this.config.id;
        }
    },
    updated() {
        this.addKeyUpHandler();
    },
    methods: {
        created: function () {
            if (this.config) {
                this.addKeyUpHandler();
                if (this.returnFocus) {
                    this.returnFocus = false;
                    this.$refs[this.ref].focusIn();
                }
            }
        },
        addKeyUpHandler() {
            if (typeof this?.$refs?.[this.ref]?.ej2Instances?.element?.parentElement !== 'undefined') {
                this.$refs[this.ref].ej2Instances.element.parentElement.removeEventListener('keyup', this.keyup, true);
                this.$refs[this.ref].ej2Instances.element.parentElement.addEventListener('keyup', this.keyup, true);
            }
        },
        keyup(ev) {
            if (ev.keyCode === 13) { // enter
                if (typeof this.$parent.processAction === 'function') { // NOSONAR
                    this.$parent.processAction('trapEnter', ev);
                } else if (this.$parent.component && typeof this.$parent.component.processAction === 'function') {
                    this.$parent.component.processAction('trapEnter', ev);
                }
            }
        },
        beforeOpen(ev) {
            if (event && event.keyCode === 13) {
                ev.cancel = true;
            }
        },
        actionComplete(ev) {
            // console.log(ev)
        },
        localChange(ev) {
            if (ev && ev.isInteracted && ev.value === null) {
                if (typeof this?.$refs?.[this.ref]?.ej2Instances?.hidePopup === 'function') {
                    this.$refs[this.ref].ej2Instances.hidePopup();
                }
            }
        },
        localClose() {
            if (typeof this?.$refs?.[this.ref]?.ej2Instances?.element?.parentElement !== 'undefined') {
                this.$refs[this.ref].ej2Instances.element.parentElement.addEventListener("focusout", () => { this.returnFocus = true; }, true);
            }
        },
        localFiltering(e) {
            let filteredOptions = this.options;
            let fProps = this.config.filterProps;
            if (!e.text || !Array.isArray(fProps) || !fProps.length) return;
            if (fProps.indexOf('any') !== -1) fProps = ['any']; // remove other key options if all is selected
            filteredOptions = filteredOptions.filter((option) => {
                let add = false;
                this.config.filterProps.forEach((prop) => {
                    if (add) return;
                    let testObj = prop === 'any' ? option : option[prop];
                    const pattern = new RegExp(e.text, 'i');
                    add = pattern.test(JSON.stringify(testObj));
                });
                return add;
            });
            e.updateData(filteredOptions);
        }
    },
    components: {
        'ejs-dropdownlist': defineAsyncComponent(() => import('@common/syncfusion/dropdownList')),
        'ejs-splitbutton': defineAsyncComponent(() => import('@common/syncfusion/splitButton')),
        'sam-tooltip': SamTooltipComponent,
        'control': defineAsyncComponent(() => import('@common/control/control.vue')),
    }
};

export default dropdownComponent;
</script>
<style scoped>
.sub-content-area {
    display: flex;
}
.sub-content-area > div {
    margin: 0 10px;
    position: relative;
    top: -4px;
}
.sub-content-area > div + div {
    margin-left: 0;
}
.sub-content-area > div.message-container {
    flex-grow: 9;
}
.secondary-action-button {
    text-decoration: underline;
    color: #0B4A97;
    padding: 5px 8px;
    background: #eee;
    font-size: 14px;
    border-radius: 0 0 6px 6px;
    white-space: nowrap;
}
:deep(.flex)  {
    width: 100%;
    display: flex;
}
:deep(.flex .dropdown-wrapper) {
    flex-grow: 12;
}
:deep(.flex .separately-secondary-action-button-wrapper) {
    margin: 0 5px;
}
</style>
